import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useUser } from "../../contexts/UserContext";
import useScrollToTop from "../../hooks/useScrollToTop";
import Button from "../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";

import MessagesReceived from "./MessagesReceived";
import MessagesSent from "./MessagesSent";
import CreateMessageForm from "./CreateMessageForm";

import { FaInbox, FaPaperPlane } from "react-icons/fa";
import { LuMessageSquarePlus } from "react-icons/lu";

/* -----------------------------
   Petites briques UI locales
----------------------------- */

function Badge({ value, tone = "violet" }) {
  if (!value || value <= 0) return null;
  const toneMap = {
    violet: "bg-violet-600",
    amber: "bg-amber-600",
    sky: "bg-sky-600",
    rose: "bg-rose-600",
    slate: "bg-slate-600",
    emerald: "bg-emerald-600",
  };
  const bg = toneMap[tone] || toneMap.violet;
  return (
    <span
      className={`${bg} text-white text-[11px] font-bold rounded-full px-2 py-[2px] leading-none`}
    >
      {value}
    </span>
  );
}

function ModuleNav({ items, activeId, onChange }) {
  return (
    <>
      {/* Mobile */}
      <div className="md:hidden -mx-2 px-2 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 pb-2">
          {items.map((it) => {
            const Active = activeId === it.id;
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                onClick={() => onChange(it.id)}
                className={`whitespace-nowrap inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ring-1 transition ${
                  Active
                    ? "bg-violet-600 text-white ring-violet-500"
                    : "bg-white/80 dark:bg-slate-900/70 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-white"
                }`}
              >
                <Icon />
                {it.title}
                <Badge value={it.badge} tone={it.badgeTone} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop */}
      <aside className="hidden md:block md:w-64">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <p className="font-semibold">Messagerie</p>
          </div>
          <nav className="p-2">
            {items.map((it) => {
              const Active = activeId === it.id;
              const Icon = it.icon;
              return (
                <button
                  key={it.id}
                  onClick={() => onChange(it.id)}
                  className={`w-full text-left flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm ring-1 transition mb-2 ${
                    Active
                      ? "bg-violet-600 text-white ring-violet-500"
                      : "bg-white/80 dark:bg-slate-900/70 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-white"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon />
                    {it.title}
                  </span>
                  <Badge value={it.badge} tone={it.badgeTone} />
                </button>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
}

function SectionShell({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
        <div>
          <h4 className="text-lg sm:text-xl font-semibold">{title}</h4>
          {subtitle && <p className="text-white/90 text-sm">{subtitle}</p>}
        </div>
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </div>
  );
}

/* -----------------------------
   Page
----------------------------- */

export default function MessagesPage() {
  useScrollToTop();
  const { user } = useUser();
  const userId = user?.userId;
  const roleId = Number(user?.role_id);

  const isAdmin = roleId === 1;
  const isRestrictedToAdmin = [2, 3, 4].includes(roleId);

  const [active, setActive] = useState("received");
  const [showForm, setShowForm] = useState(false);
  const [refreshMessages, setRefreshMessages] = useState(false);

  const [counts, setCounts] = useState({
    received: { total: 0, unread: 0 },
    sent: { total: 0 },
  });

  const setModuleCounts = useCallback((key, obj) => {
    setCounts((c) => ({ ...c, [key]: { ...c[key], ...obj } }));
  }, []);

  useEffect(() => {
    if (!userId)
      toast.error("Vous devez être connecté pour accéder à la messagerie.");
  }, [userId]);

  const modules = useMemo(
    () => [
      {
        id: "received",
        title: "Reçus",
        icon: FaInbox,
        badge: counts.received.unread || 0,
        badgeTone: "violet",
      },
      {
        id: "sent",
        title: "Envoyés",
        icon: FaPaperPlane,
        badge: 0,
      },
    ],
    [counts],
  );

  const handleSuccess = () => {
    setShowForm(false);
    setRefreshMessages(true);
    setActive("sent");
  };

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <div className="text-center space-y-2 mb-8">
          <h2 className="text-sm uppercase tracking-widest text-gray-500 dark:text-gray-400">
            ORGANISATEUR
          </h2>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            Messagerie
          </h1>
          <p>
            Vous avez des questions, des bugs constatés, vous pouvez me joindre
            par cette messagerie.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[16rem,1fr] gap-4 md:gap-6">
          <ModuleNav items={modules} activeId={active} onChange={setActive} />

          <div className="space-y-6">
            <div className="flex items-center justify-end">
              <Button
                text={
                  isRestrictedToAdmin ? "Contacter l’admin" : "Nouveau message"
                }
                onClick={() => setShowForm(true)}
                icon={LuMessageSquarePlus}
                className="gap-2 !rounded-xl"
                hoverLabel="Composer un message"
              />
            </div>

            {showForm && (
              <SectionShell
                title={
                  isRestrictedToAdmin
                    ? "Message à l’administrateur"
                    : "Nouveau message"
                }
                subtitle={
                  isRestrictedToAdmin
                    ? "Vos messages sont envoyés uniquement à l’admin."
                    : "Rédigez et envoyez un message."
                }
              >
                <CreateMessageForm
                  show={showForm}
                  onHide={() => setShowForm(false)}
                  onSuccess={handleSuccess}
                  userId={userId}
                  // userRoleId={roleId}
                  userRoleId={user?.role_id}
                  // si user 2/3/4 → le form verrouille automatiquement sur admin
                />
              </SectionShell>
            )}

            {active === "received" && (
              <SectionShell
                title="Messages reçus"
                subtitle={
                  isAdmin ? "Messages de vos clients." : "Boîte de réception."
                }
              >
                <MessagesReceived
                  userId={userId}
                  refresh={refreshMessages}
                  setRefresh={setRefreshMessages}
                  onCounts={(obj) => setModuleCounts("received", obj)}
                />
              </SectionShell>
            )}

            {active === "sent" && (
              <SectionShell
                title="Messages envoyés"
                subtitle="Historique des messages envoyés."
              >
                <MessagesSent
                  userId={userId}
                  refresh={refreshMessages}
                  setRefresh={setRefreshMessages}
                />
              </SectionShell>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
}
